import React, { useState, useEffect, useRef } from 'react';
import Navbar from '../../navbar/navbar';
import Footer from '../../footer/footer';
import profiles from './profiles'; // Your profiles data array
import Spinner from '../../spinner/spinner';
import {
    FaLightbulb,
    FaEye,
    FaUsers,
    FaHandshake,
    FaUsersCog,
} from 'react-icons/fa';
import { Helmet } from 'react-helmet';
import DPGImage from '../../../assets/images/dk.png';
import LebeneImage from '../../../assets/images/Lebene.png';
import YaliImage from '../../../assets/images/yali.png';
import ForestryImage from '../../../assets/images/1616687849-79-ghana-forestry-commission.png';
import './about.css';

const specialProfiles = ['Nicholas', 'Samuel', 'Godwin', 'Fredericka', 'Fatahiya'];

// Improved smooth count-up hook using requestAnimationFrame
function useCountUp(end = 100, duration = 2000) {
    const [count, setCount] = useState(0);
    const startTimestamp = useRef(null);

    useEffect(() => {
        function step(timestamp) {
            if (!startTimestamp.current) startTimestamp.current = timestamp;
            const progress = timestamp - startTimestamp.current;
            const progressRatio = Math.min(progress / duration, 1);
            const currentCount = Math.floor(progressRatio * end);

            setCount(currentCount);

            if (progress < duration) {
                requestAnimationFrame(step);
            } else {
                setCount(end); // Ensure exact end value at the end
            }
        }

        requestAnimationFrame(step);
    }, [end, duration]);

    return count;
}

const iconMap = {
    mission: <FaLightbulb className="inline text-indigo-600 animate-pulse" />,
    vision: <FaEye className="inline text-indigo-600" />,
    approach: <FaUsers className="inline text-indigo-600" />,
    team: <FaHandshake className="inline text-indigo-600 animate-bounce" />,
    partners: <FaUsersCog className="inline text-indigo-600" />,
};

const About = () => {
    const [showFullProfile, setShowFullProfile] = useState(
        Array(profiles.length).fill(false)
    );
    const [currentSection, setCurrentSection] = useState('');
    const [loading, setLoading] = useState(true);
    const volunteersCount = useCountUp(100, 2000);

    const toggleFullProfile = (index) => {
        setShowFullProfile((prev) => {
            const copy = [...prev];
            copy[index] = !copy[index];
            return copy;
        });
    };

    // Handle scrollspy for active section
    useEffect(() => {
        window.scrollTo(0, 0);

        const handleScroll = () => {
            const sections = document.querySelectorAll('section');
            const scrollY = window.pageYOffset;
            sections.forEach((section) => {
                if (
                    scrollY >= section.offsetTop - 96 && // Adjust for navbar height + padding
                    scrollY < section.offsetTop + section.offsetHeight - 96
                ) {
                    setCurrentSection(section.id);
                }
            });
        };

        window.addEventListener('scroll', handleScroll, { passive: true });
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    // Simulate loading spinner for UX
    useEffect(() => {
        const timer = setTimeout(() => setLoading(false), 900);
        return () => clearTimeout(timer);
    }, []);

    if (loading) return <Spinner />;

    return (
        <div
            className="min-h-screen bg-gray-50 text-gray-800 font-serif"
            style={{ fontFamily: "'Merriweather', serif" }}
        >
            <Helmet>
                <title>About - Dziko Centre for Climate Justice</title>
                <meta
                    name="description"
                    content="Learn more about our youth initiatives on climate change mitigation."
                />
                <meta property="og:title" content="About - Dziko Centre for Climate Justice" />
                <meta
                    property="og:description"
                    content="Learn about our vision, mission and dynamic team."
                />
            </Helmet>

            <Navbar />

            {/* Prevent Navbar Overlap by matching padding to navbar height */}
            <main className="pt-28 max-w-6xl mx-auto px-6">
                {/* Mission */}
                <section id="mission" className="mb-20 scroll-mt-28">
                    <h1 className="text-4xl font-bold text-indigo-900 mb-6 mt-8 flex items-center gap-3 select-none">
                        About DZIKO CENTRE FOR CLIMATE JUSTICE (DZIKO CCJ)
                    </h1>
                    <h2 className="text-3xl font-semibold mb-4 text-indigo-700 flex items-center gap-2">
                        Our Mission {currentSection === 'mission' && iconMap.mission}
                    </h2>
                    <p className="text-lg leading-relaxed text-gray-700 max-w-4xl">
                        DZIKO CCJ was founded in January 2024 with a bold mission: to empower the next
                        generation of environmental leaders and activists. We believe that young people
                        have the power to drive positive change in the world, particularly in the urgent
                        fight against climate change and environmental degradation. Our organization is
                        dedicated to providing young people with the knowledge, skills, and resources they
                        need to become effective advocates for the planet.
                    </p>
                </section>

                {/* Vision */}
                <section id="vision" className="mb-20 scroll-mt-28">
                    <h2 className="text-3xl font-semibold mb-4 text-indigo-700 flex items-center gap-2">
                        Our Vision {currentSection === 'vision' && iconMap.vision}
                    </h2>
                    <p className="text-lg leading-relaxed text-gray-700 max-w-4xl">
                        Our vision is a world where every young person is empowered to take action to
                        protect and preserve the environment. We envision a future where sustainability
                        is not just a buzzword but a way of life, and where every individual understands
                        their role in creating a more sustainable and equitable world for all.
                    </p>
                </section>

                {/* Approach */}
                <section id="approach" className="mb-20 scroll-mt-28">
                    <h2 className="text-3xl font-semibold mb-4 text-indigo-700 flex items-center gap-2">
                        Our Approach {currentSection === 'approach' && iconMap.approach}
                    </h2>
                    <p className="text-lg leading-relaxed text-gray-700 max-w-4xl">
                        At DZIKO CCJ, we take a multi-faceted approach to environmental activism and
                        education. We believe in the power of education to inspire change, so we work
                        with schools, communities, and youth organizations to provide environmental
                        education and awareness programs. We also support youth-led projects and
                        initiatives that address pressing environmental issues, from plastic pollution
                        to deforestation to climate change.
                    </p>
                </section>

                {/* Team */}
                <section id="team" className="mb-24 scroll-mt-28">
                    <h2 className="text-3xl font-semibold mb-12 text-indigo-700 flex items-center gap-2 select-none">
                        Meet Our Expert Team: Driven Professionals Crafting Sustainable Solutions
                        {currentSection === 'team' && iconMap.team}
                    </h2>

                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-12">
                        {profiles.map(({ id, name, role, image, content }) => (
                            <article
                                key={id}
                                className="bg-white rounded-2xl shadow-xl p-6 flex flex-col items-center text-center transition-shadow hover:shadow-2xl"
                                tabIndex={0}
                                aria-labelledby={`profile-name-${id}`}
                            >
                                {/* Adjust headroom padding for special profiles */}
                                <div
                                    className={`relative w-44 h-44 mb-6 rounded-full overflow-hidden border-8 border-indigo-700 shadow-lg flex justify-center items-center ${specialProfiles.includes(name)
                                            ? 'pt-8 items-start'
                                            : 'items-center'
                                        }`}
                                >
                                    <img
                                        src={image}
                                        alt={`Photo of ${name}`}
                                        loading="lazy"
                                        className="object-cover w-full h-full rounded-full"
                                    />
                                </div>

                                <h3
                                    id={`profile-name-${id}`}
                                    className="text-xl font-semibold mb-2 text-indigo-900 font-serif"
                                >
                                    {name}
                                </h3>
                                <p className="mb-6 italic text-indigo-700 font-semibold">{role}</p>

                                <button
                                    onClick={() => toggleFullProfile(id - 1)}
                                    className="mb-4 bg-indigo-700 text-white px-6 py-2 rounded-full hover:bg-indigo-600 focus:outline-none focus:ring-4 focus:ring-indigo-400 transition"
                                    aria-expanded={showFullProfile[id - 1]}
                                    aria-controls={`profile-content-${id}`}
                                >
                                    {showFullProfile[id - 1] ? 'Hide Full Profile' : 'View Full Profile'}
                                </button>

                                {showFullProfile[id - 1] && (
                                    <div
                                        id={`profile-content-${id}`}
                                        className="text-gray-700 max-h-64 overflow-y-auto text-left px-3 scrollbar-thin scrollbar-thumb-indigo-400 scrollbar-track-gray-200"
                                        style={{ scrollbarWidth: 'thin' }}
                                    >
                                        <p>{content}</p>
                                    </div>
                                )}
                            </article>
                        ))}
                    </div>

                    {/* Volunteers Counter */}
                    <div className="mt-20 bg-white rounded-2xl p-12 shadow-xl max-w-lg mx-auto text-center">
                        <p
                            className="text-indigo-900 font-extrabold text-7xl tracking-wide select-none"
                            aria-label={`Volunteers nationwide: ${volunteersCount}+`}
                        >
                            {volunteersCount}+
                        </p>
                        <p className="text-lg text-gray-700 font-semibold mt-3">
                            Volunteers Nationwide
                        </p>
                    </div>
                </section>

                {/* Partners */}
                <section id="partners" className="mb-24 scroll-mt-28">
                    <h2 className="text-3xl font-semibold mb-8 text-indigo-700 flex items-center gap-3 select-none">
                        Our Partners, Past and Present {currentSection === 'partners' && iconMap.partners}
                    </h2>
                    <p className="text-lg leading-relaxed text-gray-700 max-w-4xl mb-10">
                        DZIKO CCJ works with a diverse range of partners including businesses, non-profits,
                        government agencies, and educational institutions both past and present. These
                        collaborations help us maximize our impact and reach, leveraging collective strengths
                        and resources to drive positive environmental change.
                    </p>

                    <div className="flex flex-wrap justify-center gap-12">
                        <img
                            src={LebeneImage}
                            alt="Lebene Botanicals Partner Logo"
                            loading="lazy"
                            className="h-48 object-contain hover:scale-110 transition-transform duration-300"
                        />
                        <img
                            src={DPGImage}
                            alt="DPG Partner Logo"
                            loading="lazy"
                            className="h-40 object-contain hover:scale-110 transition-transform duration-300 mt-2"
                        />
                        <img
                            src={ForestryImage}
                            alt="Forestry Commission Partner Logo"
                            loading="lazy"
                            className="h-44 object-contain hover:scale-110 transition-transform duration-300"
                        />
                        <img
                            src={YaliImage}
                            alt="Yali Partner Logo"
                            loading="lazy"
                            className="h-28 object-contain hover:scale-110 transition-transform duration-300 mt-8"
                        />
                    </div>
                </section>

                {/* Join Us */}
                <section className="mb-32 scroll-mt-28 text-center max-w-3xl mx-auto">
                    <h2 className="text-3xl font-semibold mb-6 text-indigo-700 flex justify-center items-center gap-3 select-none">
                        Join Us {currentSection === 'team' && iconMap.team}
                    </h2>
                    <p className="text-lg text-gray-700 leading-relaxed">
                        Are you passionate about protecting the environment and making a difference in your
                        community? Join DZIKO CCJ today and become part of a growing movement of young
                        people dedicated to saving the planet. Together, we can create a brighter, greener
                        future.
                    </p>
                </section>
            </main>

            <Footer />
        </div>
    );
};

export default About;
